<template>
  <div class="anforderungen-popover">
    <span :id="'anforderungen-popover-' + _uid" class="anforderungen-popover__trigger">
      <slot></slot>
    </span>

    <!-- Hover mit den Pflichtanforderungen, gegliedert nach den Kategorien der Konfiguration -->
    <b-popover
      v-if="gruppen.length || hinweise.length"
      :target="'anforderungen-popover-' + _uid"
      triggers="hover"
      :placement="placement"
      boundary="viewport"
      customClass="anforderungen-popover__panel"
    >
      <div class="anforderungen-popover__content">
        <div v-if="titel" class="anforderungen-popover__titel">{{ titel }}</div>
        <div v-if="hinweise.length" class="anforderungen-popover__hinweise">
          <div
            v-for="(hinweis, hinweisIndex) in hinweise"
            :key="hinweisIndex"
            class="anforderungen-popover__hinweis"
          >
            <AlertCircle :size="16" class="flex-shrink-0" />
            <span>{{ hinweis }}</span>
          </div>
        </div>

        <div
          v-for="(gruppe, gruppenIndex) in gruppenMitBilanz"
          :key="gruppenIndex"
          class="anforderungen-popover__gruppe"
        >
          <div v-if="gruppe.titel || (zeigeBilanz && zeigeStatus)" class="anforderungen-popover__kopf">
            <span v-if="gruppe.titel" class="anforderungen-popover__name">{{ gruppe.titel }}</span>
            <span
              v-if="zeigeBilanz && zeigeStatus"
              class="anforderungen-popover__bilanz"
              :class="
                gruppe.fehlend > 0
                  ? 'anforderungen-popover__bilanz--fehlend'
                  : 'anforderungen-popover__bilanz--vollstaendig'
              "
            >
              {{ gruppe.bilanzText }}
            </span>
          </div>

          <div
            v-for="(abschnitt, abschnittIndex) in gruppe.abschnitte"
            :key="abschnittIndex"
            class="anforderungen-popover__abschnitt"
          >
            <div class="anforderungen-popover__abschnitt-titel">{{ abschnitt.titel }}</div>
            <div class="anforderungen-popover__eintraege">
              <div
                v-for="(eintrag, eintragIndex) in abschnitt.eintraege"
                :key="eintragIndex"
                class="anforderungen-popover__eintrag"
                :class="
                  !zeigeStatus
                    ? 'anforderungen-popover__eintrag--ohne-status'
                    : eintrag.erfuellt
                    ? 'anforderungen-popover__eintrag--erfuellt'
                    : 'anforderungen-popover__eintrag--fehlend'
                "
              >
                <template v-if="zeigeStatus">
                  <Check v-if="eintrag.erfuellt" :size="14" />
                  <CircleAlert v-else :size="14" />
                </template>
                <span v-else class="anforderungen-popover__marker"></span>
                <span>{{ eintrag.label }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </b-popover>
  </div>
</template>

<script>
import { AlertCircle, Check, CircleAlert } from 'lucide-vue';

export default {
  name: 'AnforderungenPopoverComponent',
  components: {
    AlertCircle,
    Check,
    CircleAlert,
  },
  props: {
    // Kopfzeile des Popovers, entfällt wenn nicht gesetzt
    titel: { type: String, default: null },
    // [{ titel, abschnitte: [{ titel, eintraege: [{ label, erfuellt }] }] }]
    gruppen: { type: Array, default: () => [] },
    // Gelb hervorgehobene Hinweise oberhalb der Gruppen, als Liste von Texten
    hinweise: { type: Array, default: () => [] },
    placement: { type: String, default: 'left' },
    // Blendet die Bilanz je Gruppe aus, etwa wenn nur die benötigten Daten gelistet werden
    zeigeBilanz: { type: Boolean, default: true },
    // Blendet Haken, Alert und die Erfüllt-Färbung aus, wenn die Einträge keinen Status tragen
    zeigeStatus: { type: Boolean, default: true },
  },
  computed: {
    // Ergänzt je Gruppe die Anzahl fehlender Einträge und den daraus abgeleiteten Text
    gruppenMitBilanz() {
      return this.gruppen.map(gruppe => {
        const eintraege = (gruppe.abschnitte || []).flatMap(abschnitt => abschnitt.eintraege || []);
        const fehlend = eintraege.filter(eintrag => !eintrag.erfuellt).length;
        return {
          ...gruppe,
          abschnitte: gruppe.abschnitte || [],
          fehlend,
          bilanzText: fehlend > 0 ? `${fehlend} von ${eintraege.length} fehlen` : 'vollständig',
        };
      });
    },
  },
};
</script>

<style scoped>
.anforderungen-popover__trigger {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  cursor: pointer;
}
</style>

<style>
.anforderungen-popover__panel.popover {
  max-width: 450px;
  border: 1px solid var(--border);
  border-radius: var(--radius-sm);
  box-shadow: var(--shadow-lg);
}

.anforderungen-popover__panel .popover-body {
  max-height: 480px;
  padding: 16px;
  overflow-y: auto;
}

.anforderungen-popover__titel {
  margin-bottom: 12px;
  font-size: 16px;
  line-height: 24px;
  font-weight: 600;
  color: var(--text);
}

.anforderungen-popover__hinweise {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  margin-bottom: 12px;
}

.anforderungen-popover__hinweise:last-child {
  margin-bottom: 0;
}

.anforderungen-popover__hinweis {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  max-width: 100%;
  padding: 8px 16px;
  border: 0.5px solid var(--warning);
  border-radius: var(--radius-sm);
  background-color: var(--warning-bg);
  color: var(--warning-text);
  font-size: 12px;
  line-height: 18px;
  font-weight: 500;
  box-shadow: var(--shadow-sm);
}

.anforderungen-popover__hinweis > svg {
  display: block;
}

.anforderungen-popover__gruppe + .anforderungen-popover__gruppe {
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid var(--border);
}

.anforderungen-popover__kopf {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 8px;
}

.anforderungen-popover__name {
  font-size: 14px;
  line-height: 20px;
  font-weight: 600;
  color: var(--text);
}

.anforderungen-popover__bilanz {
  flex-shrink: 0;
  font-size: 12px;
  line-height: 16px;
  font-weight: 500;
}

.anforderungen-popover__bilanz--fehlend {
  color: var(--danger);
}

.anforderungen-popover__bilanz--vollstaendig {
  color: var(--success);
}

.anforderungen-popover__abschnitt + .anforderungen-popover__abschnitt {
  margin-top: 8px;
}

.anforderungen-popover__abschnitt-titel {
  margin-bottom: 4px;
  font-size: 12px;
  line-height: 16px;
  font-weight: 600;
  text-transform: uppercase;
  color: var(--text-muted);
}

.anforderungen-popover__eintraege {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4px 16px;
}

.anforderungen-popover__eintrag {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  line-height: 20px;
}

.anforderungen-popover__eintrag > svg {
  flex-shrink: 0;
}

.anforderungen-popover__eintrag--erfuellt {
  color: var(--success);
}

.anforderungen-popover__eintrag--erfuellt span {
  color: var(--text-body);
}

.anforderungen-popover__eintrag--fehlend {
  color: var(--danger);
}

/* Punkt statt Statusicon, damit die Zeilen ohne Haken und Alert einen linken Anker behalten */
.anforderungen-popover__marker {
  flex-shrink: 0;
  width: 4px;
  height: 4px;
  border-radius: 50%;
  background-color: var(--text-muted);
}

.anforderungen-popover__eintrag--ohne-status {
  gap: 4px;
  color: var(--text-body);
}

@media (max-width: 480px) {
  .anforderungen-popover__eintraege {
    grid-template-columns: 1fr;
  }
}
</style>
